import { SyntheticEvent, useCallback, useRef } from 'react';

/**
 * we have two grids in the campaign mapping body view (left and right) and a header scroll.
 * we need to sync the scroll position of the two grids and the header scroll.
 * @returns An object containing the left and right grid refs, the header scroll ref, and the functions to sync the scroll position of the two grids and the header scroll.
 */
export const useSyncedGridScroll = () => {
  const leftGridRef = useRef(null);
  const rightGridRef = useRef(null);
  const headerScrollRef = useRef<HTMLDivElement | null>(null);
  const isSyncingRef = useRef(false);

  const syncFromTop = useCallback((p: SyntheticEvent) => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    const rightEl = (rightGridRef.current as { element?: HTMLElement } | null)
      ?.element;
    if (rightEl && p.target instanceof HTMLElement) {
      rightEl.scrollLeft = p.target.scrollLeft;
    }
    isSyncingRef.current = false;
  }, []);

  const syncFromRight = useCallback((p: SyntheticEvent) => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;

    if (headerScrollRef.current && p.target instanceof HTMLElement) {
      headerScrollRef.current.scrollLeft = p.target.scrollLeft;
    }

    const leftEl = (leftGridRef.current as { element?: HTMLElement } | null)
      ?.element;
    if (
      leftEl &&
      p.target instanceof HTMLElement &&
      leftEl.scrollTop !== p.target.scrollTop
    ) {
      leftEl.scrollTop = p.target.scrollTop;
    }
    isSyncingRef.current = false;
  }, []);

  const syncFromLeft = useCallback((p: SyntheticEvent) => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;

    const rightEl = (rightGridRef.current as { element?: HTMLElement } | null)
      ?.element;
    if (
      rightEl &&
      p.target instanceof HTMLElement &&
      rightEl.scrollTop !== p.target.scrollTop
    ) {
      rightEl.scrollTop = p.target.scrollTop;
    }
    isSyncingRef.current = false;
  }, []);

  return {
    leftGridRef,
    rightGridRef,
    headerScrollRef,
    syncFromTop,
    syncFromRight,
    syncFromLeft,
  };
};
